import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../Components/Sidebar';
import '../Style/EditProduct.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaCloudUploadAlt, FaSpinner, FaTimes } from 'react-icons/fa';

import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';

const MAX_IMG_MB = 10;
const MAX_FILE_MB = 25;

// ONE accept list that includes BOTH images + files
const UPLOAD_ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx,.zip,.rar,.7z,image/*';

const isISODate = v => /^\d{4}-\d{2}-\d{2}$/.test(v);
const fmtDate = d => {
  if (!d) return '';
  if (typeof d === 'string') return isISODate(d) ? d : d.slice(0, 10);
  if (d.toDate) return d.toDate().toISOString().slice(0, 10);
  if (d instanceof Date) return d.toISOString().slice(0, 10);
  return '';
};

const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

const isBlockedAttachment = (file) => {
  const name = (file?.name || '').toLowerCase().trim();
  const blockedExt = ['.exe', '.msi', '.apk', '.bat', '.cmd', '.com', '.scr', '.js', '.vbs', '.ps1', '.sh'];
  return blockedExt.some(ext => name.endsWith(ext));
};

const safeFilename = (name) => (name || 'file').replace(/[^\w.\-() ]+/g, '_');
const uidPart = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export default function EditProduct() {
  const { id } = useParams(); // Firestore doc id
  const navigate = useNavigate();

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Existing uploads from DB
  const [existingFiles, setExistingFiles] = useState([]); // [{name, storedAs, url, size, type, kind}]
  const [existingImageUrl, setExistingImageUrl] = useState('');

  // NEW uploads (images + files) in ONE picker
  const [uploads, setUploads] = useState([]);
  const uploadInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  // Form state (+ ✅ NEW oemPrice)
  const [form, setForm] = useState({
    productId: '',
    inHousePart: '',
    name: '',
    description: '',
    dateCreated: '',
    oemPart: '',
    quantity: '',
    costPrice: '',
    oemPrice: '',     // ✅ NEW
    markupPct: '',
    discountPct: ''
  });

  // Errors
  const [errors, setErrors] = useState({
    inHousePart: false,
    name: false,
    dateCreated: false,
    quantity: false,
    costPrice: false
  });

  // Derived pricing
  const derived = useMemo(() => {
    const qty = parseFloat(form.quantity) || 0;
    const cost = parseFloat(form.costPrice) || 0;

    const mRaw = parseFloat(form.markupPct) || 0;
    const dRaw = parseFloat(form.discountPct) || 0;

    const m = clamp(mRaw, 0, 100000);
    const d = clamp(dRaw, 0, 100);

    const unitBefore = cost;
    const unitAfter = cost * (1 + m / 100);
    const unitAfterDiscount = unitAfter * (1 - d / 100);

    const totalBefore = unitBefore * qty;
    const totalAfter = unitAfter * qty;
    const totalAfterDiscount = unitAfterDiscount * qty;

    const fix = (n) => Number((Number.isFinite(n) ? n : 0).toFixed(2));

    return {
      unitBefore: fix(unitBefore),
      unitAfter: fix(unitAfter),
      unitAfterDiscount: fix(unitAfterDiscount),
      totalBefore: fix(totalBefore),
      totalAfter: fix(totalAfter),
      totalAfterDiscount: fix(totalAfterDiscount),
      normalizedMarkupPct: fix(m),
      normalizedDiscountPct: fix(d),
    };
  }, [form.quantity, form.costPrice, form.markupPct, form.discountPct]);

  // Load product
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'Products', id));
        if (!snap.exists()) {
          toast.error('Product not found.', { className: 'toast-error' });
          navigate('/products');
          return;
        }
        const data = snap.data();

        setForm({
          productId: data.productId || '',
          inHousePart: data.inHousePart || '',
          name: data.name || '',
          description: data.description === '—' ? '' : (data.description || ''),
          dateCreated: fmtDate(data.dateCreated) || '',
          oemPart: data.oemPart === '—' ? '' : (data.oemPart || ''),
          quantity: data.quantity === undefined ? '' : String(data.quantity),
          costPrice: data.costPrice === undefined ? '' : String(data.costPrice),
          oemPrice: data.oemPrice === undefined ? '' : String(data.oemPrice), // ✅ NEW
          markupPct: data.markupPct === undefined ? '' : String(data.markupPct),
          discountPct: data.discountPct === undefined ? '' : String(data.discountPct),
        });

        setExistingImageUrl(data.imageUrl || '');
        setExistingFiles(Array.isArray(data.files) ? data.files : []);
      } catch (err) {
        console.error(err);
        toast.error('Error loading product.', { className: 'toast-error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  // Validation
  const validate = useCallback(() => {
    const newErr = {
      inHousePart: !form.inHousePart.trim(),
      name: !form.name.trim(),
      dateCreated: !form.dateCreated,
      quantity: !(parseFloat(form.quantity) >= 0),
      costPrice: !(parseFloat(form.costPrice) >= 0),
    };
    setErrors(newErr);
    return !Object.values(newErr).some(Boolean);
  }, [form]);

  // Handlers
  const setField = (name, value) => setForm(p => ({ ...p, [name]: value }));

  // --- Unified upload helpers (images + files) ---
  const pickUploads = () => uploadInputRef.current?.click();

  const addUploads = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const next = [];

    for (const f of files) {
      if (!f) continue;

      if (isBlockedAttachment(f)) {
        toast.error(`Blocked file type: ${f.name}`, { className: 'toast-error' });
        continue;
      }

      const isImg = (f.type || '').startsWith('image/');
      const limitMb = isImg ? MAX_IMG_MB : MAX_FILE_MB;

      if (f.size > limitMb * 1024 * 1024) {
        toast.error(`"${f.name}" exceeds ${limitMb} MB.`, { className: 'toast-error' });
        continue;
      }

      const existsNew = uploads.some(u => u.file?.name === f.name && u.file?.size === f.size);
      if (existsNew) continue;

      const previewUrl = isImg ? URL.createObjectURL(f) : '';
      next.push({ file: f, previewUrl });
    }

    if (next.length) setUploads(prev => [...prev, ...next]);
  };

  const onUploadInput = (e) => {
    addUploads(e.target.files);
    e.target.value = '';
  };

  const onDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const onDragLeave = () => setDragActive(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    addUploads(e.dataTransfer.files);
  };

  const removeNewUploadAt = (idx) => {
    setUploads(prev => {
      const item = prev[idx];
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearNewUploads = () => {
    setUploads(prev => {
      prev.forEach(u => { if (u?.previewUrl) URL.revokeObjectURL(u.previewUrl); });
      return [];
    });
  };

  // cleanup previews on unmount
  useEffect(() => {
    return () => {
      uploads.forEach(u => { if (u?.previewUrl) URL.revokeObjectURL(u.previewUrl); });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const primaryImagePreview = useMemo(() => {
    const img = uploads.find(u => (u.file?.type || '').startsWith('image/'));
    return img?.previewUrl || existingImageUrl || null;
  }, [uploads, existingImageUrl]);

  const removeExistingFileAt = (idx) => {
    setExistingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const removeExistingImage = () => {
    setExistingImageUrl('');
  };

  const uploadNewItemsIfAny = async () => {
    if (!uploads.length) return { uploaded: [], primaryNewImageUrl: '' };

    const results = await Promise.all(
      uploads.map(async (u) => {
        const f = u.file;
        const storedAs = `${uidPart()}_${safeFilename(f.name)}`;
        const storageRef = ref(storage, `Products/${form.productId}/Uploads/${storedAs}`);

        await uploadBytes(storageRef, f, { contentType: f.type || 'application/octet-stream' });
        const url = await getDownloadURL(storageRef);

        return {
          name: f.name,
          storedAs,
          url,
          size: f.size,
          type: f.type || '',
          kind: (f.type || '').startsWith('image/') ? 'image' : 'file',
        };
      })
    );

    const primary = results.find(r => r.kind === 'image')?.url || '';
    return { uploaded: results, primaryNewImageUrl: primary };
  };

  // Submit
  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please correct highlighted fields.', { className: 'toast-error' });
      return;
    }

    setSaving(true);
    try {
      const { uploaded, primaryNewImageUrl } = await uploadNewItemsIfAny();
      const mergedFiles = [...existingFiles, ...uploaded];

      let nextImageUrl = '';
      if (primaryNewImageUrl) nextImageUrl = primaryNewImageUrl;
      else if (existingImageUrl) nextImageUrl = existingImageUrl;
      else nextImageUrl = mergedFiles.find(f => f.kind === 'image')?.url || '';

      await updateDoc(doc(db, 'Products', id), {
        productId: form.productId,
        inHousePart: form.inHousePart.trim(),
        name: form.name.trim(),
        description: form.description.trim() || '—',
        dateCreated: form.dateCreated,
        oemPart: form.oemPart.trim() || '—',

        quantity: form.quantity === '' ? 0 : Number(parseFloat(form.quantity).toFixed(2)),
        costPrice: form.costPrice === '' ? 0 : Number(parseFloat(form.costPrice).toFixed(2)),

        // ✅ NEW
        oemPrice: form.oemPrice === '' ? 0 : Number(parseFloat(form.oemPrice).toFixed(2)),

        markupPct: form.markupPct === '' ? 0 : Number(parseFloat(form.markupPct).toFixed(2)),
        discountPct: form.discountPct === '' ? 0 : Number(parseFloat(form.discountPct).toFixed(2)),

        unitBeforeProfit: derived.unitBefore,
        unitAfterProfit: derived.unitAfter,
        unitAfterDiscount: derived.unitAfterDiscount,
        sellingPrice: derived.unitAfterDiscount,

        totalBeforeProfit: derived.totalBefore,
        totalAfterProfit: derived.totalAfter,
        totalAfterDiscount: derived.totalAfterDiscount,

        files: mergedFiles,
        imageUrl: nextImageUrl || '',

        updatedAt: new Date().toISOString(),
      });

      toast.success('Product updated successfully!', { className: 'toast-success' });
      navigate('/products');
    } catch (err) {
      console.error(err);
      toast.error('Update failed.', { className: 'toast-error' });
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => navigate('/products');

  if (loading) {
    return (
      <div className="editproduct-page">
        <Sidebar />
        <div className="editproduct-loading">
          <FaSpinner className="spin" size={48} />
          <p>Loading product…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="editproduct-page">
      <ToastContainer
        position="top-right"
        autoClose={2600}
        toastClassName={({ type }) => (type === 'success' ? 'toast-success' : 'toast-error')}
      />
      <Sidebar />

      <main className="editproduct-main">
        <h2>Edit Product</h2>

        <form className="editproduct-form" onSubmit={submit}>
          {/* Basic Info */}
          <section className="panel">
            <div className="field-row three-cols">
              <div className="field-group">
                <label>Product ID</label>
                <input value={form.productId} readOnly title="Auto-generated product code" />
              </div>

              <div className="field-group">
                <label>In-House Part No. <span className="required">*</span></label>
                <input
                  value={form.inHousePart}
                  onChange={e => setField('inHousePart', e.target.value)}
                  className={errors.inHousePart ? 'input-error' : ''}
                  aria-invalid={errors.inHousePart}
                />
                {errors.inHousePart && <span className="error-text">Required</span>}
              </div>

              <div className="field-group">
                <label>Stock Item Name <span className="required">*</span></label>
                <input
                  value={form.name}
                  onChange={e => setField('name', e.target.value)}
                  className={errors.name ? 'input-error' : ''}
                  aria-invalid={errors.name}
                />
                {errors.name && <span className="error-text">Required</span>}
              </div>
            </div>

            <div className="field-row three-cols">
              <div className="field-group">
                <label>Date Created <span className="required">*</span></label>
                <input
                  type="date"
                  value={fmtDate(form.dateCreated)}
                  onChange={e => setField('dateCreated', e.target.value)}
                  className={errors.dateCreated ? 'input-error' : ''}
                  aria-invalid={errors.dateCreated}
                />
                {errors.dateCreated && <span className="error-text">Required</span>}
              </div>

              <div className="field-group">
                <label>OEM Part No.</label>
                <input
                  value={form.oemPart}
                  onChange={e => setField('oemPart', e.target.value)}
                />
              </div>

              <div className="field-group full-span">
                <label>Description</label>
                <textarea
                  rows="3"
                  value={form.description}
                  onChange={e => setField('description', e.target.value)}
                  placeholder="General product description…"
                />
              </div>
            </div>
          </section>

          {/* Inventory & Pricing */}
          <section className="panel">
            <div className="section-head">
              <h3>Inventory & Pricing</h3>
            </div>

            <div className="field-row three-cols">
              <div className="field-group">
                <label>Quantity <span className="required">*</span></label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.quantity}
                  onChange={e => setField('quantity', e.target.value)}
                  className={errors.quantity ? 'input-error' : ''}
                />
                {errors.quantity && <span className="error-text">Required</span>}
              </div>

              <div className="field-group">
                <label>Cost Price <span className="required">*</span></label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.costPrice}
                  onChange={e => setField('costPrice', e.target.value)}
                  className={errors.costPrice ? 'input-error' : ''}
                />
                {errors.costPrice && <span className="error-text">Required</span>}
              </div>

              {/* ✅ NEW OEM Price */}
              <div className="field-group">
                <label>OEM Price</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.oemPrice}
                  onChange={e => setField('oemPrice', e.target.value)}
                  placeholder="0.00"
                  title="Optional: price from OEM / supplier"
                />
              </div>
            </div>

            <div className="field-row three-cols">
              <div className="field-group">
                <label>Mark-up %</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.markupPct}
                  onChange={e => setField('markupPct', e.target.value)}
                />
              </div>

              <div className="field-group">
                <label>Discount %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.discountPct}
                  onChange={e => setField('discountPct', e.target.value)}
                  placeholder="e.g. 10"
                  title="Applies to the marked-up selling price"
                />
              </div>
            </div>

            <div className="calc-grid">
              <div className="metric">
                <span className="lbl">Unit (Before Profit)</span>
                <span className="val">R{derived.unitBefore.toFixed(2)}</span>
              </div>
              <div className="metric">
                <span className="lbl">Unit (After Profit)</span>
                <span className="val">R{derived.unitAfter.toFixed(2)}</span>
              </div>
              <div className="metric">
                <span className="lbl">Unit (After Discount)</span>
                <span className="val">R{derived.unitAfterDiscount.toFixed(2)}</span>
              </div>

              <div className="metric">
                <span className="lbl">Total (Before Profit)</span>
                <span className="val">R{derived.totalBefore.toFixed(2)}</span>
              </div>
              <div className="metric">
                <span className="lbl">Total (After Profit)</span>
                <span className="val">R{derived.totalAfter.toFixed(2)}</span>
              </div>
              <div className="metric strong">
                <span className="lbl">Total (After Discount)</span>
                <span className="val">R{derived.totalAfterDiscount.toFixed(2)}</span>
              </div>

              <div className="field-group readonly-sp">
                <label>Selling Price (Auto)</label>
                <input
                  readOnly
                  value={derived.unitAfterDiscount.toFixed(2)}
                  title="Auto-calculated: Cost Price × (1 + Mark-up % / 100) × (1 - Discount % / 100)"
                />
              </div>
            </div>
          </section>

          {/* Unified Uploads (Images + Files) */}
          <section className="panel">
            <h3>Product Uploads</h3>
            <p className="hint">
              Upload images and/or files together. Images max {MAX_IMG_MB} MB each. Files max {MAX_FILE_MB} MB each.
            </p>

            <div
              className={`upload-area ${dragActive ? 'drag-active' : ''}`}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={pickUploads}
              role="button"
              aria-label="Upload files"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter') pickUploads(); }}
            >
              <FaCloudUploadAlt size={32} />
              <p>{dragActive ? 'Drop to add' : 'Click or drag uploads here'}</p>
              <small>Images • Files • One uploader</small>
              <input
                ref={uploadInputRef}
                type="file"
                accept={UPLOAD_ACCEPT}
                multiple
                onChange={onUploadInput}
                style={{ display: 'none' }}
              />
            </div>

            {primaryImagePreview && (
              <div className="preview-row">
                <div className="image-box">
                  <img src={primaryImagePreview} alt="Product" />
                  {existingImageUrl && !uploads.some(u => (u.file?.type || '').startsWith('image/')) && (
                    <button
                      type="button"
                      className="remove-thumb"
                      onClick={removeExistingImage}
                      aria-label="Remove existing image"
                      title="Remove existing image"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>
            )}

            {existingFiles.length > 0 && (
              <div className="mt">
                <div className="hint" style={{ marginBottom: 10 }}>
                  Existing uploads: <strong>{existingFiles.length}</strong>
                </div>

                <div className="file-list">
                  {existingFiles.map((f, idx) => (
                    <div key={`${f.storedAs || f.url || f.name}-${idx}`} className="file-row">
                      <div className="file-meta">
                        <div className="file-name" title={f.name}>
                          {f.kind === 'image' ? '🖼️ ' : '📄 '}
                          {f.name}
                        </div>
                        <div className="file-sub">
                          {typeof f.size === 'number' ? `${(f.size / (1024 * 1024)).toFixed(2)} MB` : ''}{' '}
                          {f.type ? `• ${f.type}` : ''}
                        </div>
                        {f.url && (
                          <a
                            href={f.url}
                            target="_blank"
                            rel="noreferrer"
                            className="file-link"
                            style={{ marginTop: 6, display: 'inline-block' }}
                          >
                            Open
                          </a>
                        )}
                      </div>

                      <button
                        type="button"
                        className="btn-clear"
                        onClick={() => removeExistingFileAt(idx)}
                        title="Remove from product (will save on Save Changes)"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                      >
                        <FaTimes />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {uploads.length > 0 && (
              <div className="mt">
                <div className="hint" style={{ marginBottom: 10 }}>
                  New uploads to add: <strong>{uploads.length}</strong>
                </div>

                <div className="file-list">
                  {uploads.map((u, idx) => {
                    const f = u.file;
                    const isImg = (f.type || '').startsWith('image/');
                    return (
                      <div key={`${f.name}-${f.size}-${idx}`} className="file-row">
                        <div className="file-meta">
                          <div className="file-name" title={f.name}>
                            {isImg ? '🖼️ ' : '📄 '}
                            {f.name}
                          </div>
                          <div className="file-sub">
                            {(f.size / (1024 * 1024)).toFixed(2)} MB {f.type ? `• ${f.type}` : ''}
                          </div>
                        </div>

                        <button
                          type="button"
                          className="btn-clear"
                          onClick={() => removeNewUploadAt(idx)}
                          title="Remove"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                        >
                          <FaTimes />
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    className="btn-clear"
                    onClick={clearNewUploads}
                    disabled={saving}
                  >
                    Clear New Uploads
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Actions */}
          <section className="panel actions-panel">
            <div className="save-section">
              <button type="submit" className="btn-submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button
                type="button"
                className="btn-clear"
                onClick={() => window.location.reload()}
                disabled={saving}
              >
                Reset
              </button>
              <button type="button" className="btn-cancel" onClick={cancel} disabled={saving}>
                Cancel
              </button>
            </div>
          </section>
        </form>
      </main>

      {saving && (
        <div className="saving-overlay">
          <FaSpinner className="spin" size={56} />
          <p>Updating product…</p>
        </div>
      )}
    </div>
  );
}
